// Copyright (c) Docugami, Inc. All rights reserved.

import { Modal, TooltipHost } from "@fluentui/react";
import { ChatView, selectChatOptions } from "components/chat/ChatView";
import { LoadingImage, LoadingPlaceholder } from "components/common/LoadingPlaceholder";
import { IconRenderer } from "components/work_path/IconRenderer"
import { ReportTable } from "components/reports/ReportTable";
import { getDefaultViewModeForReport } from "components/reports/ReportsHelpers";
import { publishReportThunk } from "components/reports/reportsActions";
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper";
import { ChunkSourceLegend } from "hooks/ChunkSelectionColor";
import { useFeatureFlag } from "hooks/FeatureFlags";
import { useModalSelectionAreaDefinition, useModalSelectionAreas } from "hooks/ModalSelection";
import { useTargetReportDocument } from "hooks/WorkPathTargetDocument";
import { useInitializer } from "hooks/deepLinkInitializer";
import { DocumentDgmlContext, useDocumentDgmlContext } from "hooks/documentDgml";
import { NavigationType, ReportViewMode, parseReportQueryParams, useNavigationService } from "hooks/navigationService";
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useReportsContextProvider } from "hooks/reports";
import { useWorkPathReportLabels } from "hooks/useWorkPathTypeName";
import { NamedResource, WorkPathType } from "networking/WorkPathApi";
import { DocumentEvaluationStatus, ReportSettings, ReportViewContext } from "networking/reportsApi";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { AppDispatch, AppState } from "store";
import { useLazyInitializeDocSetChatQuery } from "store/chat/chatSlice";
import { ModalSelectionArea } from "store/documents/types";
import { FeatureFlags } from "store/featureFlags/types";
import { useGetExampleChunksQuery } from "store/frontend/frontendSlice";
import { isInlineChatEnabled } from "store/organization/organizationSlice";
import { createOperationId, reportDocumentsAdapter, reportsApiSlice, selectPatchedReportSchemaState, selectGetReviewDocumentsState, setReportQuery } from "store/reports/reportsSlice";
import { createSelectFilteredReportDocuments, createSelectFilteredReportEntries, selectPublishedDocumentsState, selectReport, selectReportContext, selectReportDocumentId, selectReportDocumentsView, selectReportId, selectReportSchema, selectReportViewMode } from "store/reports/selectors";
import { setCurrentDocument } from "store/sdvSlice";
import styled from "styled-components";
import { TelemetryEvent, WorkPathPublishedSchema } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { WorkPathCanvasProps } from "work_path/WorkPathCanvas";
import { AbstractPreview } from "./AbstractPreview";
import { PublishConfirmation } from "./PublishConfirmation";
import { ReportDocumentViewer } from "./ReportDocumentViewer";
import { StepHeader } from "./StepHeader";
import { ReportHeader } from "./WorkPathHeaders";
import { WorkPathStepCanvas } from "./WorkPathStepCanvas";
import { isAbstractViewEnabled } from "./WorkPathsHelper";
import Split from 'react-split';
import "./styles/ReportDesignerCanvas.scss";
import { getReportsChatCollapsed, getReportsChatWidth as getReportsChatWidthPercent, setReportsChatCollapsed, setReportsChatWidth as setReportsChatWidthPercent } from "utils/localStorageHelpers";

// Set the page container to hide overflow, and content to scroll on the x-axis
// This isn't the cleanest solution, but will ensure that the report designer
// does not grow wider than the app window, since its contents are wide
const StepContainer = styled.div`
    flex-direction: column;
    display: flex;
    flex: 1;
    min-height: 0;
    width: 100%;
    overflow: hidden;
`;

const ContentContainer = styled.div`
    flex: 1;
    min-height: 0;
    overflow-x: auto;
    position: relative;
`;

const GridContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
`;

const OverlayContainer = styled.div<{ isVisible: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    background: white;
    display: ${props => props.isVisible ? 'block' : 'none'};
`;

const OuterGridContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    left: 0;
    top: 0;
`;

const OuterChatContainer = styled.div<{ chatCollapsed: boolean, collapsedWidth: number, disabled?: boolean }>`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    right: 0;
    top: 0;
    minWidth: ${props => props.chatCollapsed ? props.collapsedWidth : undefined},
    maxWidth: ${props => props.chatCollapsed ? props.collapsedWidth : undefined},
    overflow: ${props => props.chatCollapsed ? "hidden" : undefined},
    padding: 0;
    ${props => props.disabled ? "pointer-events: none; opacity: 0.6;" : ""}
`;

const OpenChatPanelButton = styled.div<{ collapsed?: boolean, collapsedWidth?: number }>`
    position: absolute;
    top: 12px;
    ${({ collapsed }) => collapsed ? 'right: 0;' : 'left: -28px;'}
    z-index: 5;
    width: ${props => props.collapsedWidth}px;
    height: 36px;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: background 0.15s, border 0.15s;
    opacity: 0.85;
    &:hover {
        background: #e0e0e0;
        opacity: 1;
    }
    svg {
        width: 16px;
        height: 16px;
        fill: #666;
    }
`;

const CloseChatPanelButton = styled.button`
  position: absolute;
  left: -28px;
  top: 10px;
  z-index: 5;
`;

const SplitContainer = styled.div<{ visible: boolean }>`
    display: ${props => (props.visible ? "block" : "none")};
    height: 100%;
`;

// Helper to update gutter styles based on chatCollapsed
const updateGutterElementsStyles = (collapsed: boolean, gutters: any[]) => {
    gutters.forEach(gutter => {
        if (gutter instanceof HTMLElement) {
            gutter.style.pointerEvents = collapsed ? 'none' : 'auto';
            gutter.style.cursor = collapsed ? 'default' : 'col-resize';
        }
    });
};

const updateGutterStyles = (collapsed: boolean) => {
    const gutters = document.querySelectorAll('.gutter');
    updateGutterElementsStyles(collapsed, Array.from(gutters));
};

// Since the report grid is now always rendered, we can create the selector for the grid documents and entries here
// These can be used in any place in reports where we want to access the current view documents/entries
export const selectFilteredReportGridDocuments = createSelectFilteredReportDocuments();
export const selectFilteredReportGridEntries = createSelectFilteredReportEntries();

interface ReportDesignerCanvasProps extends WorkPathCanvasProps {
    setLastDocument: (documentId: string) => void;
    lastDocumentId: string;
}

export const ReportDesignerCanvas = (props: ReportDesignerCanvasProps): JSX.Element => {
    const reportId = useAppSelector(state => state.workPaths.currentWorkPath!.resources[NamedResource.ReportId]);
    const viewMode = useAppSelector(state => state.reportsSlice.viewMode);
    const context = useAppSelector(selectReportContext);
    const documentId = useAppSelector(selectReportDocumentId);
    const queryInstanceId = useAppSelector(state => state.reportsSlice.queryInstanceId);
    const reportSchema = useAppSelector(selectReportSchema);

    useInitializer({ clusterId: props.workPath.clusterId, documentId: documentId, instanceId: queryInstanceId });

    const location = useLocation();
    const navigation = useNavigationService();
    const dispatch = useAppDispatch();

    const dgmlContext = useDocumentDgmlContext(documentId);
    const reportLabels = useWorkPathReportLabels(props.workPath.workPathType);
    useReportsContextProvider({
        clusterId: props.workPath.clusterId,
        reportId: reportId,
        reportItemLabel: reportLabels.itemsName,
        reportTitleLabel: reportLabels.titleName
    });

    const chatEnabled = useFeatureFlag(FeatureFlags.ChatFeature);
    const chatOptions = useAppSelector(state => selectChatOptions(state, props.workPath.clusterId));
    const [triggerInitializeChat, { isUninitialized: isChatUninitialized }] = useLazyInitializeDocSetChatQuery();

    useEffect(() => {
        if (viewMode === ReportViewMode.Chat && isChatUninitialized && !!chatOptions) {
            triggerInitializeChat(chatOptions);
        }
    }, [viewMode, triggerInitializeChat, isChatUninitialized, chatOptions])

    useEffect(() => {
        dispatch(setCurrentDocument(documentId))
    }, [documentId, dispatch]);

    useEffect(() => {
        dispatch(setReportQuery(parseReportQueryParams(location.search)))
    }, [location.search, dispatch]);

    useEffect(() => {
        // Set the default context for the report if nothing is set
        if ((!context || !viewMode) && !!reportSchema?.report) {
            const mode = !!viewMode && (chatEnabled || viewMode !== ReportViewMode.Chat) ? viewMode :
                getDefaultViewModeForReport(reportSchema.report, props.workPath.workPathType);
            navigation.navToReportPage(
                NavigationType.ReplaceCurrentPage,
                {
                    context: reportSchema.report.isPublished ? ReportViewContext.Published : ReportViewContext.Review,
                    mode: mode,
                    documentId: documentId,
                    instanceId: queryInstanceId
                }
            )
        }
    }, [context, reportSchema?.report, documentId, queryInstanceId, viewMode, navigation, props.workPath.workPathType, chatEnabled]);

    return (
        <DocumentDgmlContext.Provider value={dgmlContext}>
            <ReportDesignerCanvasInternal {...props} />
        </DocumentDgmlContext.Provider>
    );
}


interface ReportDesignerCanvasInternalProps extends ReportDesignerCanvasProps {
}

/**
 * Presents UI to the user for designing a report
 */
const ReportDesignerCanvasInternal = (props: ReportDesignerCanvasInternalProps): JSX.Element => {
    const reportId = useAppSelector(selectReportId);
    const viewMode = useAppSelector(selectReportViewMode);
    const documentsView = useAppSelector(selectReportDocumentsView);
    const publishedDocumentsState = useAppSelector(selectPublishedDocumentsState);
    const currentContext = useAppSelector(selectReportContext);
    const report = useAppSelector(selectReport);
    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType)
    useGetExampleChunksQuery({ docsetId: props.workPath.clusterId, includeText: false });

    const targetDocumentId = useAppSelector(selectReportDocumentId);
    useTargetReportDocument(props.lastDocumentId);
    const documentIds = useMemo(() => documentsView?.ids.filter(id => !!documentsView?.entities[id]?.clusterDocument.hasCpml), [documentsView]);

    const navigation = useNavigationService()

    const [cachedIsPublished, setCachedIsPublished] = useState<boolean | undefined>(report?.isPublished);

    const { execute: executePublish, state: publishRequestState } = useAsyncHelper<void>("Publish report batch", undefined, () => setShowPublishConfirmation(false));

    const currentDocument = documentsView?.entities[targetDocumentId];

    const canvasRef = useRef<HTMLDivElement | null>(null);
    useModalSelectionAreas(targetDocumentId);
    useModalSelectionAreaDefinition(ModalSelectionArea.Canvas, canvasRef);

    const [showPublishConfirmation, setShowPublishConfirmation] = useState(false)

    const abstractViewEnabled = isAbstractViewEnabled(props.workPath.workPathType);

    const autoPublish = report?.autoPublishDocuments;
    const dispatch = useAppDispatch();
    const defaultViewMode = useMemo(() => !!report && !!workPathType ? getDefaultViewModeForReport(report, workPathType) : undefined, [report, workPathType])
    const inlineChatEnabled = useAppSelector(isInlineChatEnabled)

    useEffect(() => {
        // Ensure view mode is valid
        if (!abstractViewEnabled && viewMode === ReportViewMode.Abstract && !!defaultViewMode) {
            navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
                mode: defaultViewMode
            });
        }
    }, [viewMode, abstractViewEnabled, navigation, defaultViewMode]);

    useEffect(() => {
        // Don't allow you to be in the published view for an unpublished report
        if (currentContext === ReportViewContext.Published && report?.isPublished === false) {
            navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
                context: ReportViewContext.Review,
                documentId: '',
                instanceId: ''
            });
        }
    }, [currentContext, navigation, report?.isPublished]);

    useEffect(() => {
        // When auto publish is enabled, go to the published view
        if (currentContext === ReportViewContext.Review && autoPublish === true && report?.isPublished === true && publishRequestState !== RequestState.InProgress) {
            navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
                context: ReportViewContext.Published,
                documentId: '',
                instanceId: ''
            });
        }
    }, [currentContext, autoPublish, report?.isPublished, navigation, publishRequestState]);

    useEffect(() => {
        // If in review mode and there are no docs to review - nav to the published view
        if (currentContext === ReportViewContext.Review && report?.isPublished === true && documentsView?.ids.length === 0 && publishRequestState !== RequestState.InProgress) {
            navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
                context: ReportViewContext.Published,
                mode: ReportViewMode.Table,
                documentId: '',
                instanceId: ''
            });
        }
    }, [currentContext, documentsView?.ids, navigation, publishRequestState, report?.isPublished]);

    useEffect(() => {
        // If in published mode and there are no published docs - nav to the review view
        if (currentContext === ReportViewContext.Published && report?.isPublished === true && publishedDocumentsState?.ids.length === 0) {
            navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
                context: ReportViewContext.Review,
                mode: ReportViewMode.Table,
                documentId: '',
                instanceId: ''
            });
        }
    }, [currentContext, publishedDocumentsState?.ids, navigation, report?.isPublished]);

    useEffect(() => {
        if (viewMode === ReportViewMode.Document && currentDocument?.state.evaluationStatus === DocumentEvaluationStatus.Confirmed) {
            navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
                mode: ReportViewMode.Table
            });
        }
    }, [currentDocument?.state.evaluationStatus, navigation, viewMode])

    useEffect(() => {
        props.setLastDocument(targetDocumentId);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [targetDocumentId]);

    useEffect(() => {
        // Initialize the isPublished state and if it changes, trigger a navigation
        if (report?.isPublished !== undefined) {
            if (cachedIsPublished === undefined) {
                setCachedIsPublished(report?.isPublished);
            }
            else if (cachedIsPublished !== report?.isPublished && publishRequestState !== RequestState.InProgress) {
                // Published state of the report has changed - nav appropriately
                navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
                    mode: ReportViewMode.Table,
                    context: autoPublish ? ReportViewContext.Published : ReportViewContext.Review,
                    documentId: '',
                    instanceId: ''
                });

                setCachedIsPublished(report?.isPublished);
            }
        }
    }, [report?.isPublished, cachedIsPublished, navigation, publishRequestState, autoPublish]);

    function onContextChange(context: ReportViewContext) {
        if (!currentContext || currentContext === context) {
            return;
        }

        navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
            context
        });
    }

    const renderGrid = () => {
        return (
            <GridContainer>
                <ChunkSourceLegend />
                <ReportTable currentContext={currentContext} onContextChange={onContextChange} />
            </GridContainer>
        );
    };

    const MAX_CHAT_WIDTH_PERCENT = 35;
    const COLLAPSED_CHAT_WIDTH_PIXELS = 36;

    const [chatCollapsed, setChatCollapsed] = useState(getReportsChatCollapsed());
    const [chatSize, setChatSize] = useState(getReportsChatWidthPercent());

    // Use cachedIsPublished directly
    const shouldCollapseChat = chatCollapsed || cachedIsPublished === true;
    const chatDisabled = cachedIsPublished === true;
    const chatTooltip = chatDisabled
        ? "Chat is disabled because this report is published. Edit the report to enable chat."
        : "Use chat to build your report.";

    useEffect(() => {
        setReportsChatCollapsed(chatCollapsed);
        updateGutterStyles(shouldCollapseChat);
    }, [chatCollapsed, shouldCollapseChat]);

    useEffect(() => {
        setReportsChatWidthPercent(chatSize);
    }, [chatSize]);

    const handleCollapse = () => setChatCollapsed(true);
    const handleExpand = () => setChatCollapsed(false);

    const sizes = shouldCollapseChat
        ? [100 - (COLLAPSED_CHAT_WIDTH_PIXELS * 100) / window.innerWidth, (COLLAPSED_CHAT_WIDTH_PIXELS * 100) / window.innerWidth]
        : [100 - chatSize, chatSize];

    const renderGridPanel = () => {
        if (inlineChatEnabled) {
            return (
                <ContentContainer>
                    <SplitContainer visible={viewMode === ReportViewMode.Table}>
                        <Split
                            sizes={sizes}
                            minSize={[0, shouldCollapseChat ? COLLAPSED_CHAT_WIDTH_PIXELS : 300]}
                            maxSize={[Infinity, 600]}
                            direction="horizontal"
                            gutterSize={8}
                            gutter={() => {
                                const el = document.createElement('div');
                                el.className = 'gutter custom-split-gutter';
                                updateGutterElementsStyles(shouldCollapseChat, [el]);
                                return el;
                            }}
                            onDragEnd={([grid, chat]) => {
                                if (!shouldCollapseChat) setChatSize(Math.min(chat, MAX_CHAT_WIDTH_PERCENT));
                            }}
                            className={'split'}
                        >
                            <OuterGridContainer>
                                {renderGrid()}
                            </OuterGridContainer>
                            <TooltipHost content={chatTooltip} calloutProps={{ gapSpace: 0 }}>
                                <OuterChatContainer
                                    chatCollapsed={shouldCollapseChat}
                                    collapsedWidth={COLLAPSED_CHAT_WIDTH_PIXELS}
                                    disabled={chatDisabled}
                                    aria-disabled={chatDisabled}
                                >
                                    {!shouldCollapseChat && (
                                        <CloseChatPanelButton
                                            onClick={handleCollapse}
                                            title="Collapse chat panel"
                                        >&lt;</CloseChatPanelButton>
                                    )}
                                    {!shouldCollapseChat && (
                                        <ChatView docSetId={props.workPath.clusterId} reportId={reportId} />
                                    )}
                                    {shouldCollapseChat && (
                                        <OpenChatPanelButton
                                            collapsed={shouldCollapseChat}
                                            collapsedWidth={COLLAPSED_CHAT_WIDTH_PIXELS}
                                            onClick={handleExpand}
                                        >
                                            <IconRenderer source={{ name: 'chat', height: COLLAPSED_CHAT_WIDTH_PIXELS - 12, width: COLLAPSED_CHAT_WIDTH_PIXELS - 12 }} width={COLLAPSED_CHAT_WIDTH_PIXELS - 8} height={COLLAPSED_CHAT_WIDTH_PIXELS - 8} />
                                        </OpenChatPanelButton>
                                    )}
                                </OuterChatContainer>
                            </TooltipHost>
                        </Split>
                    </SplitContainer>
                    <OverlayContainer isVisible={viewMode !== ReportViewMode.Table}>
                        {renderOverlay()}
                    </OverlayContainer>
                </ContentContainer>
            );
        } else {
            return (
                <ContentContainer>
                    {renderGrid()}
                    <OverlayContainer isVisible={viewMode !== ReportViewMode.Table}>
                        {renderOverlay()}
                    </OverlayContainer>
                </ContentContainer>
            );
        }
    };

    const renderOverlay = () => {
        switch (viewMode) {
            case ReportViewMode.Abstract:
                return (
                    <AbstractPreview
                        documentIds={documentIds}
                        workPathName={props.workPath.name}
                        targetDocumentId={targetDocumentId}
                    />
                );
            case ReportViewMode.Document:
                return (
                    <ReportDocumentViewer
                        targetDocumentId={targetDocumentId}
                        documentIds={documentIds}
                    />
                );
            case ReportViewMode.Chat:
                return <ChatView docSetId={props.workPath.clusterId} />;
            default:
                return null;
        }
    };

    return (
        <LoadingPlaceholder
            isLoading={!targetDocumentId}
            loadingPlaceholder={<LoadingImage />}>
            <WorkPathStepCanvas>
                {showPublishConfirmation && (
                    <Modal styles={{ main: { borderRadius: '10px', width: '90%' } }} isOpen={showPublishConfirmation} isBlocking={false} onDismiss={() => setShowPublishConfirmation(false)} >
                        <PublishConfirmation onConfirm={autoConfirm => executePublish(dispatch(publishReportBatch(reportId!, autoConfirm)))} onCancel={() => setShowPublishConfirmation(false)} autoPublish={!!autoPublish} isBusy={publishRequestState === RequestState.InProgress} />
                    </Modal>
                )}
                <>
                    <ReportHeader
                        documentId={targetDocumentId}
                        workPath={props.workPath}
                        onPublish={() => setShowPublishConfirmation(true)} />
                    <StepContainer>
                        <StepHeader />
                        {renderGridPanel()}
                    </StepContainer >
                </>
            </WorkPathStepCanvas >
        </LoadingPlaceholder>
    )
};

const publishReportBatch = (reportId: string, autoConfirm: boolean) => async (dispatch: AppDispatch, getState: () => AppState): Promise<void> => {
    const state = getState();
    const workPathType = state.workPaths.currentWorkPath!.workPathType;

    const reportState = selectPatchedReportSchemaState(state, reportId);
    const documentState = selectGetReviewDocumentsState(state, reportId)?.documents;
    if (!reportState || !documentState?.ids.length) {
        return;
    }

    const report = reportState.report;

    const settings: ReportSettings = {
        autoPublishDocuments: autoConfirm,
        exportAbstract: workPathType === WorkPathType.Abstract,
        createDefaultAbstractTemplate: workPathType === WorkPathType.Abstract
    };

    if (!report.isPublished) {
        await dispatch(publishReportThunk(
            reportId,
            settings,
            undefined,
            createOperationId()
        ));
    }
    else {
        const reviewFolderIds = reportDocumentsAdapter.getSelectors()
            .selectAll(documentState)
            .filter(x => !!state.documents.indexed[x.folderId]?.clusterDocument.hasCpml)
            .map(x => x.folderId);

        const confirmPromise = dispatch(reportsApiSlice.endpoints.confirmDocuments.initiate({
            reportId: reportId,
            folderIds: reviewFolderIds,
            operationId: createOperationId()
        }));

        const promises: Promise<any>[] = [confirmPromise];
        if (autoConfirm !== report.autoPublishDocuments) {
            const updateReportPromise: Promise<any> = dispatch(reportsApiSlice.endpoints.updateReportSettings.initiate({
                reportId: reportId,
                reportSettings: settings,
                operationId: createOperationId()
            }));

            promises.push(updateReportPromise);
        }

        await Promise.allSettled(promises);
    }

    Telemetry.trackEvent<WorkPathPublishedSchema>({
        eventName: TelemetryEvent.WorkPathPublished
    });
}
